import {
  AwardPointsPerInput,
  AwardVouchersInput,
  Earning_Mechanism_Product_Counting_Attribute,
  Earning_Mechanism_Product_Counting_Rounding_Strategy,
  EarningMechanismConfigurationInput,
  EarningMechanismLoyaltyEventInput,
  EarningMechanismStoredValueDiscountInput,
  EarningMechanismProductDiscountInput,
  EarningMechanismTransactionDiscountInput,
  EarningMechanismVarietyDiscountInput,
  Membership_Event_Type,
  ProductGroupFragment,
  PromocodesOfferType,
  Random_Award_Type,
} from '@occc/graphql';

import {
  AwardInputType,
  AwardPointsOrVouchersInput,
  AwardPointsOrVouchersOrNullInput,
} from './types';

import { GameConfigureFormValues } from '../routes/Game/types';

class EarningMechanismInputCreator {
  public createProductPurchase(
    purchaseThreshold: number,
    eligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[],
    awardInput: AwardInputType,
    countProductsBy?: Earning_Mechanism_Product_Counting_Attribute,
  ): { productPurchase: NonNullable<EarningMechanismConfigurationInput['productPurchase']> } {
    const eligibleProductGroupIds = eligibleProductGroups?.map((item) => item.id);
    const countProductsByValue = countProductsBy
      ? countProductsBy
      : Earning_Mechanism_Product_Counting_Attribute.Quantity;

    return {
      productPurchase: {
        purchaseThreshold,
        countProductsBy: countProductsByValue,
        eligibleProductGroupIds,
        ...(awardInput ?? {}),
      },
    };
  }

  public createPointSpend(
    pointCost: number,
    awardVouchers: AwardVouchersInput,
    offerIsReturnable?: boolean,
    allowedForNewMember?: boolean,
  ): { pointSpend: NonNullable<EarningMechanismConfigurationInput['pointSpend']> } {
    return {
      pointSpend: {
        pointCost,
        awardVouchers,
        offerIsReturnable,
        allowedForNewMember,
      },
    };
  }

  public createTotalSpendOverTime(
    accumulationThreshold: number,
    ineligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[] | undefined,
    awardInput: AwardInputType,
    stayVisibleWhileInProgress?: boolean | null,
  ): { totalSpendOverTime: NonNullable<EarningMechanismConfigurationInput['totalSpendOverTime']> } {
    const ineligibleProductGroupIds = ineligibleProductGroups?.map((item) => item.id) || [];

    return {
      totalSpendOverTime: {
        accumulationThreshold,
        ineligibleProductGroupIds,
        stayVisibleWhileInProgress,
        ...awardInput,
      },
    };
  }

  public createTotalSpendOnTransaction(
    roundMoneyAmount: Earning_Mechanism_Product_Counting_Rounding_Strategy,
    ineligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[],
    awardPointsPer: AwardPointsPerInput,
  ): {
    totalSpendOnTransaction: NonNullable<
      EarningMechanismConfigurationInput['totalSpendOnTransaction']
    >;
  } {
    const ineligibleProductGroupIds = ineligibleProductGroups?.map((item) => item.id);

    return {
      totalSpendOnTransaction: {
        roundMoneyAmount,
        ineligibleProductGroupIds,
        awardPointsPer,
      },
    };
  }

  public createTotalEligiblePurchase(
    countProductsBy: Earning_Mechanism_Product_Counting_Attribute,
    roundCountedProducts: Earning_Mechanism_Product_Counting_Rounding_Strategy,
    eligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[],
    awardPointsPer: AwardPointsPerInput,
  ): {
    totalEligiblePurchase: NonNullable<EarningMechanismConfigurationInput['totalEligiblePurchase']>;
  } {
    const eligibleProductGroupIds = eligibleProductGroups?.map((item) => item.id);

    return {
      totalEligiblePurchase: {
        countProductsBy,
        roundCountedProducts,
        eligibleProductGroupIds,
        awardPointsPer,
      },
    };
  }

  public createVisits(
    visitThreshold: number,
    awardInput: AwardInputType,
  ): { visits: NonNullable<EarningMechanismConfigurationInput['visits']> } {
    return {
      visits: {
        visitThreshold,
        ...awardInput,
      },
    };
  }

  public createVarietyPurchase(
    products: {
      eligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[];
      countProductsBy: Earning_Mechanism_Product_Counting_Attribute;
      purchaseThreshold: number;
    }[],
    awardInput: AwardInputType,
  ): { varietyPurchase: NonNullable<EarningMechanismConfigurationInput['varietyPurchase']> } {
    return {
      varietyPurchase: {
        products: products?.map(
          ({ purchaseThreshold, countProductsBy, eligibleProductGroups }) => ({
            purchaseThreshold,
            countProductsBy,
            eligibleProductGroupIds: eligibleProductGroups?.map((item) => item.id),
          }),
        ),
        ...awardInput,
      },
    };
  }

  public createVarietyPurchaseOverTime(
    products: {
      eligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[];
      countProductsBy: Earning_Mechanism_Product_Counting_Attribute;
      purchaseThreshold: number;
    }[],
    awardInput: AwardInputType,
    preventDoublePunches?: boolean,
    stayVisibleWhileInProgress?: boolean | null,
    hiddenUntilProgress?: boolean,
  ): {
    varietyPurchaseOverTime: NonNullable<
      EarningMechanismConfigurationInput['varietyPurchaseOverTime']
    >;
  } {
    return {
      varietyPurchaseOverTime: {
        products: products?.map(
          ({ purchaseThreshold, countProductsBy, eligibleProductGroups }) => ({
            purchaseThreshold,
            countProductsBy,
            eligibleProductGroupIds: eligibleProductGroups?.map((item) => item.id),
          }),
        ),
        ...awardInput,
        preventDoublePunches,
        stayVisibleWhileInProgress,
        hiddenUntilProgress,
      },
    };
  }

  public createTransactionDiscount(
    purchaseAmountThreshold: number,
    ineligibleProductGroupIds: string[],
    awardInput: Pick<
      EarningMechanismTransactionDiscountInput,
      'awardTransactionMoneyDiscount' | 'awardTransactionPercentDiscount'
    >,
    allowMultipleFulfillmentsPerProductGroupThreshold?: boolean,
  ): {
    transactionDiscount: NonNullable<EarningMechanismConfigurationInput['transactionDiscount']>;
  } {
    const { awardTransactionMoneyDiscount, awardTransactionPercentDiscount } = awardInput;

    return {
      transactionDiscount: {
        purchaseAmountThreshold,
        ineligibleProductGroupIds,
        awardTransactionMoneyDiscount,
        awardTransactionPercentDiscount,
        allowMultipleFulfillmentsPerProductGroupThreshold,
      },
    };
  }

  public createProductDiscount(
    purchaseThreshold: number,
    eligibleProductGroupIds: string[],
    awardInput: Pick<
      EarningMechanismProductDiscountInput,
      'awardMoneyDiscount' | 'awardCappedMoneyDiscount' | 'awardPriceOverride' | 'awardBuyAndGet'
    >,
    allowCarryOver?: boolean,
  ): {
    productDiscount: NonNullable<EarningMechanismConfigurationInput['productDiscount']>;
  } {
    const { awardMoneyDiscount, awardCappedMoneyDiscount, awardPriceOverride, awardBuyAndGet } =
      awardInput;

    return {
      productDiscount: {
        purchaseThreshold,
        eligibleProductGroupIds,
        awardMoneyDiscount,
        awardCappedMoneyDiscount,
        awardPriceOverride,
        awardBuyAndGet,
        allowCarryOver,
      },
    };
  }

  public createVarietyDiscount(
    purchases: EarningMechanismVarietyDiscountInput['purchases'],
    awardInput: Pick<
      EarningMechanismVarietyDiscountInput,
      'awardProductDiscount' | 'awardTransactionMoneyDiscount' | 'awardPriceOverride'
    >,
  ): {
    varietyDiscount: NonNullable<EarningMechanismConfigurationInput['varietyDiscount']>;
  } {
    const { awardProductDiscount, awardTransactionMoneyDiscount, awardPriceOverride } = awardInput;

    return {
      varietyDiscount: {
        purchases,
        awardProductDiscount,
        awardTransactionMoneyDiscount,
        awardPriceOverride,
      },
    };
  }

  public createLoyaltyEvent(
    eventType: EarningMechanismLoyaltyEventInput['eventType'],
    awardInput: AwardInputType,
  ): {
    loyaltyEvent: NonNullable<EarningMechanismConfigurationInput['loyaltyEvent']>;
  } {
    return {
      loyaltyEvent: {
        eventType,
        ...awardInput,
      },
    };
  }

  public createStoredValueDiscount(input: EarningMechanismStoredValueDiscountInput): {
    storedValueDiscount: NonNullable<EarningMechanismConfigurationInput['storedValueDiscount']>;
  } {
    const excludedProductGroupIds = input.excludedProductGroupIds?.filter(Boolean);
    return {
      storedValueDiscount: {
        maxDiscountPerTransaction: input.maxDiscountPerTransaction,
        ...(excludedProductGroupIds?.length ? { excludedProductGroupIds } : {}),
      },
    };
  }

  public createExternalFulfillment(
    awardInput: AwardPointsOrVouchersOrNullInput,
  ): EarningMechanismConfigurationInput {
    return {
      externalFulfillment: {
        ...awardInput,
      },
    };
  }

  public createMembershipEvent(
    eventType: Membership_Event_Type,
    awardInput: AwardPointsOrVouchersInput,
  ): EarningMechanismConfigurationInput {
    return {
      membershipEvent: {
        eventType,
        ...awardInput,
      },
    };
  }

  public createTotalEligiblePurchaseOverTime(
    accumulationThreshold: number,
    countProductsBy: Earning_Mechanism_Product_Counting_Attribute,
    eligibleProductGroupIds: string[],
    awardInput: AwardPointsOrVouchersInput,
    hiddenUntilProgress?: boolean,
  ): EarningMechanismConfigurationInput {
    return {
      totalEligiblePurchaseOverTime: {
        accumulationThreshold,
        countProductsBy,
        eligibleProductGroupIds,
        hiddenUntilProgress,
        ...awardInput,
      },
    };
  }

  // TODO replace with "specific params" type for this function
  public createSpinWheelGame(values: GameConfigureFormValues): EarningMechanismConfigurationInput {
    return {
      spinWheelGame: {
        purchasableOfferId: values.purchasableOffer?.id || null,
        awardRandomPrize: {
          awards: values.segments.map((segment, index) => ({
            awardPoints:
              segment.type === Random_Award_Type.Points && segment.awardPoints
                ? { points: segment.awardPoints }
                : null,
            awardType: segment.type,
            awardVouchers:
              segment.type === Random_Award_Type.Vouchers && segment.awardOffer
                ? {
                    voucherCount: segment.awardOfferVoucherCount || 1,
                    voucheredOfferId: segment.awardOffer.id,
                    voucherExpirationDays: segment.awardOfferExpirationDays || null,
                  }
                : null,
            limit: segment.limit,
            sequenceNumber: index + 1,
          })),
        },
      },
    };
  }

  public createPromocodes(
    awardInput: AwardPointsOrVouchersInput | null,
    codesType: PromocodesOfferType,
  ): EarningMechanismConfigurationInput {
    return {
      promocodes: {
        codesType,
        ...(awardInput ?? {}),
      },
    };
  }
}

export const earningMechanismInputCreator = new EarningMechanismInputCreator();
